import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Layers, Clock } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFab } from "@/components/site/WhatsAppFab";
import { SECTORS, getSector } from "@/data/sectors";
import { useSeo } from "@/hooks/useSeo";
import { whatsappHref } from "@/config";

export default function SectorPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const sector = getSector(slug);

  useSeo({
    title: sector?.metaTitle || "Setor não encontrado | AxioForm 3D",
    description: sector?.metaDescription,
    path: `/setores/${slug}`,
  });

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!sector) {
    return (
      <div className="min-h-screen bg-ax-bg">
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 py-32 text-center">
          <h1 className="font-heading text-3xl font-bold text-ax-text">Setor não encontrado</h1>
          <Link to="/" className="text-ax-cyan mt-4 inline-block">Voltar ao início</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = sector.icon;

  return (
    <div className="min-h-screen bg-ax-bg" data-testid={`sector-page-${sector.slug}`}>
      <Navbar />

      <section className="relative overflow-hidden ax-grid border-b border-ax-border/60">
        <div className="absolute inset-0 bg-gradient-to-b from-ax-bg/40 via-ax-bg/80 to-ax-bg" />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-ax-cyan/10 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-14 pb-16 lg:pt-20 lg:pb-20">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ax-muted hover:text-ax-cyan mb-8">
            <ArrowLeft className="w-4 h-4" /> Início
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="w-12 h-12 rounded-xl bg-ax-orange/10 border border-ax-orange/30 flex items-center justify-center mb-6">
              <Icon className="w-6 h-6 text-ax-orange" />
            </div>
            <span className="font-mono text-xs uppercase tracking-widest text-ax-cyan">{sector.name} · Portugal</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-ax-text mt-3 leading-[1.05]">
              {sector.hero}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-ax-sub max-w-2xl leading-relaxed">{sector.intro}</p>
            <p className="mt-4 text-ax-text font-medium">{sector.kicker}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="sector-cta"
                onClick={() => navigate("/#orcamento")}
                className="inline-flex items-center justify-center gap-2 bg-ax-orange hover:bg-ax-orangehover text-white font-semibold px-6 py-3.5 rounded-lg transition-all hover:scale-[1.03] hover:ax-glow"
              >
                Envie a sua ideia / ficheiro <ArrowRight className="w-4 h-4" />
              </button>
              <a href={whatsappHref()} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-ax-border hover:border-ax-cyan text-ax-text font-semibold px-6 py-3.5 rounded-lg transition-all hover:bg-ax-card">
                Falar por WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="font-heading text-2xl font-bold text-ax-text mb-6">Aplicações típicas</h2>
          <ul className="space-y-3">
            {sector.applications.map((a) => (
              <li key={a} className="flex items-start gap-3 text-ax-sub">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" /> {a}
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2 className="font-heading text-2xl font-bold text-ax-text mb-6">Materiais recomendados</h2>
          <div className="flex flex-wrap gap-2.5">
            {sector.materials.map((m) => (
              <span key={m} className="inline-flex items-center gap-1.5 font-mono text-sm px-3 py-1.5 rounded-lg bg-ax-card border border-ax-border text-ax-text">
                <Layers className="w-3.5 h-3.5 text-ax-orange" /> {m}
              </span>
            ))}
          </div>
          <div className="mt-8 bg-ax-card border border-ax-border rounded-xl p-5 flex items-center gap-3">
            <Clock className="w-5 h-5 text-ax-cyan shrink-0" />
            <p className="text-sm text-ax-sub">Resposta a pedidos de orçamento em <strong className="text-ax-text">menos de 2 horas úteis</strong>. Produção 100% em Portugal.</p>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div className="border-t border-ax-border/60 pt-10">
          <h3 className="font-heading text-lg font-semibold text-ax-text mb-5">Outros setores</h3>
          <div className="flex flex-wrap gap-3">
            {SECTORS.filter((s) => s.slug !== sector.slug).map((s) => (
              <Link key={s.slug} to={`/setores/${s.slug}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-ax-card border border-ax-border text-ax-sub hover:text-ax-text hover:border-ax-cyan/50 transition-colors">
                <s.icon className="w-4 h-4 text-ax-orange" /> {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
